import React, { createContext, useCallback, useContext, useState } from 'react';

const ConfirmContext = createContext(() => Promise.resolve(false));
const PromptContext = createContext(() => Promise.resolve(null));

export const ConfirmProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const [promptValue, setPromptValue] = useState('');

  const confirm = useCallback((message, options = {}) => (
    new Promise(resolve => setState({ mode: 'confirm', message, ...options, resolve }))
  ), []);

  const prompt = useCallback((message, options = {}) => (
    new Promise(resolve => {
      setPromptValue(options.defaultValue || '');
      setState({ mode: 'prompt', message, ...options, resolve });
    })
  ), []);

  const resolveWith = useCallback(result => {
    setState(prev => {
      prev?.resolve(result);
      return null;
    });
  }, []);

  const isPrompt = state?.mode === 'prompt';

  return (
    <ConfirmContext.Provider value={confirm}>
      <PromptContext.Provider value={prompt}>
        {children}
        {state && (
          <div
            className='fixed inset-0 z-[1000] flex items-center justify-center bg-coms-ink/50 px-4'
            onClick={() => resolveWith(isPrompt ? null : false)}
          >
            <div
              className='w-full max-w-[420px] rounded-2xl border border-coms-line bg-coms-surface p-5 shadow-2xl'
              onClick={e => e.stopPropagation()}
            >
              {state.title && <h3 className='mb-2 text-[16px] font-bold text-coms-ink'>{state.title}</h3>}
              <p className='text-[14px] leading-6 text-coms-ink-2'>{state.message}</p>
              {isPrompt && (
                <input
                  autoFocus
                  type='text'
                  value={promptValue}
                  onChange={e => setPromptValue(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && resolveWith(promptValue)}
                  placeholder={state.placeholder || ''}
                  className='mt-3 h-11 w-full rounded-lg border border-coms-line bg-coms-surface px-3 text-[14px] text-coms-ink outline-none focus:border-coms-accent'
                />
              )}
              <div className='mt-5 flex justify-end gap-2'>
                <button
                  type='button'
                  onClick={() => resolveWith(isPrompt ? null : false)}
                  className='rounded-lg px-4 py-2 text-[13px] font-semibold text-coms-ink-2 hover:bg-coms-surface-2'
                >
                  {state.cancelLabel || 'Cancel'}
                </button>
                <button
                  type='button'
                  onClick={() => resolveWith(isPrompt ? promptValue : true)}
                  disabled={isPrompt && !promptValue.trim()}
                  className={`rounded-lg border px-4 py-2 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
                    state.tone === 'danger'
                      ? 'border-coms-bad-soft bg-coms-bad-soft text-coms-bad hover:opacity-90'
                      : 'border-transparent bg-coms-accent text-coms-accent-ink hover:opacity-90'
                  }`}
                >
                  {state.confirmLabel || 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}
      </PromptContext.Provider>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => useContext(ConfirmContext);
export const usePrompt = () => useContext(PromptContext);
